import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { externalUsersApi } from '@/features/external-users/api/externalUsersApi';

export interface ExternalUserSociety {
  societyId: string;
  societyName: string;
  isPrimary: boolean;
  assignedAt: Date;
}

/**
 * Recuperer les societes assignees a un utilisateur externe
 */
export const useExternalUserSocieties = (userId: string) => {
  return useQuery({
    queryKey: ['externalUserSocieties', userId],
    queryFn: () => externalUsersApi.getSocieties(userId),
    enabled: !!userId,
  });
};

/**
 * Assigner une societe a un utilisateur externe
 */
export const useAssignSocietyToExternalUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      societyId,
      isPrimary = false,
    }: {
      userId: string;
      societyId: string;
      isPrimary?: boolean;
    }) => externalUsersApi.assignSociety(userId, societyId, isPrimary),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['externalUserSocieties', variables.userId] });
      queryClient.invalidateQueries({ queryKey: ['external-users'] });
      queryClient.invalidateQueries({ queryKey: ['external-user', variables.userId] });
    },
  });
};

/**
 * Desassigner une societe d'un utilisateur externe
 */
export const useUnassignSocietyFromExternalUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      societyId,
    }: {
      userId: string;
      societyId: string;
    }) => externalUsersApi.unassignSociety(userId, societyId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['externalUserSocieties', variables.userId] });
      queryClient.invalidateQueries({ queryKey: ['external-users'] });
      queryClient.invalidateQueries({ queryKey: ['external-user', variables.userId] });
    },
  });
};

/**
 * Definir une societe comme primaire pour un utilisateur externe
 */
export const useSetPrimarySocietyForExternalUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      societyId,
    }: {
      userId: string;
      societyId: string;
    }) => externalUsersApi.setPrimarySociety(userId, societyId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['externalUserSocieties', variables.userId] });
      queryClient.invalidateQueries({ queryKey: ['external-users'] });
      queryClient.invalidateQueries({ queryKey: ['external-user', variables.userId] });
    },
  });
};
